import { ConfiguratorType } from "~/Configurators/ConfiguratorType";
import { sendCarriageUpdate } from "~/Configurators/Carriage/api/sendCarriageConfiguratorUpdate";
import { sendCarriageMiscChange, sendCarriageStepChange } from "~/Configurators/Carriage/api/sendCarriageStepChange";
import { sendCarriageDataReset } from "~/Configurators/Carriage/api/sendCarriageStepReset";
import { sendCarriageResetConfigurator } from "~/Configurators/Carriage/api/sendCarriageResetConfigurator";
import { sendCarriageAddToCart } from "~/Configurators/Carriage/api/sendCarriageAddToCart";
import { CarriageSummaryData } from "~/Configurators/Carriage/v2/configuration/kwlt/summary/KWLTCarriageSummaryData";
import type { RootState } from "~/redux/store";
import {
  BASE_DESIGNATION_SEPARATOR,
  CENTRAL_CONNECTORS_LABEL,
  CENTRAL_CONNECTORS_VALUE,
  MANUAL_CONNECTORS_LABEL,
  MANUAL_CONNECTORS_VALUE,
} from "~/Configurators/Linear/constants";
import { KWLT_CARRIAGE_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";
import { type ConfiguratorPageDefinition, type StepHardcodedData } from "~/Configurators/ConfiguratorPageDefinition";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import * as CarriageCstic from "~/Configurators/Carriage/v2/configuration/kwlt/KWLTCarriageCstics";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/Configurators/Shaft/shaftGaHelper";
import { getCsticsValue } from "~/Configurators/common/v2/helper";
import { findSelectedTileIndex } from "~/Configurators/Linear/utils";
import { type LinearConfiguration, type LinearSubstep } from "~/Configurators/Linear/models";
import { validateFromDefinition } from "~/Configurators/common/v2/validation";
import { getStep1SummaryData, getStep2SummaryData, prepareSubStepsInfo } from "./summary/KWLTSummaryDataProvider";
import { toMapFn } from "~/utils/reducers";
import { prepareSummaryDataName, prepareSummaryDataValue } from "~/Configurators/common/v2/gaHelper";
import { SubstepType } from "~/Configurators/Linear/dtos";

const CARRIAGE_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      let step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      const substeps = [SubstepType.Series, SubstepType.Size];
      step1Data = [...prepareSubStepsInfo(summaryDataProvider, substeps)!, ...step1Data];

      const selectionData = toMapFn(step1Data, prepareSummaryDataName, prepareSummaryDataValue);

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(1), selectionData);
    },
  },
  {
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      const step2Data = getStep2SummaryData(summaryDataProvider) ?? [];

      const selectionData = toMapFn(step2Data, prepareSummaryDataName, prepareSummaryDataValue);

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(2), selectionData);
    },
  },
];

export const KWLT_CARRIAGE_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.Carriage,
  productCode: KWLT_CARRIAGE_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.carriage.name",
  },
  apiCalls: {
    update: sendCarriageUpdate,
    reset: sendCarriageResetConfigurator,
    stepChange: sendCarriageStepChange,
    stepReset: sendCarriageDataReset,
    miscChange: sendCarriageMiscChange,
    addToCart: sendCarriageAddToCart,
  },
  summary: CarriageSummaryData,
  isAddToCartEnabled: (state: RootState) => {
    const kmats = state.linearConfigurator.kmats;

    const conflicts = state.linearConfigurator.conflicts;
    const validations = [0, 1].map(
      (step) =>
        state.linearConfigurator.steps[step].substeps?.every(
          (substep) =>
            validateFromDefinition(state.linearConfigurator.substeps[substep] as LinearSubstep, kmats, conflicts)
              .substepValid,
        ) ?? false,
    );
    const addToCartEnabled = validations.every((validation) => validation);
    return addToCartEnabled;
  },
  isSaveEnabled: (state: RootState) => {
    return KWLT_CARRIAGE_DEFINITION.isAddToCartEnabled(state);
  },
  stepHardcodedData: CARRIAGE_CONFIGURATOR_STEP_DATA,
  generateDesignation: (kmats) => {
    const kmat = getMainKmat({ kmats });

    const [materialName, sizeCode, version] = getCsticsValue(
      kmat,
      CarriageCstic.DesignType_MaterialName,
      CarriageCstic.DesignType_SizeCode,
      CarriageCstic.DesignType_Version,
    );

    const base = `${materialName || ""}${sizeCode || ""}`;
    const designation = version ? `${base}-${version}` : base;

    return designation || BASE_DESIGNATION_SEPARATOR;
  },
  gaConfiguratorName: "kllt-carriage",
  modelAndUpdatePreprocessor: (model, dto) => {
    const mainKmat = getMainKmat({ kmats: model.kmats });
    const cstics = mainKmat.cstics;

    const lubricationTypeCstic = mainKmat.cstics[CarriageCstic.LubricationConnectorType];
    if (lubricationTypeCstic) {
      const mapping: Record<string, string> = {
        [MANUAL_CONNECTORS_VALUE]: MANUAL_CONNECTORS_LABEL,
        [CENTRAL_CONNECTORS_VALUE]: CENTRAL_CONNECTORS_LABEL,
      };
      lubricationTypeCstic.values?.forEach((val) => {
        val.label = mapping[val.value];
      });
    }

    const designValue = cstics[CarriageCstic.MaterialName]?.value;
    const designSuffix = "." + designValue?.replace("/./g", "");

    const kitsLeftCstic = cstics[CarriageCstic.KitsLeft];
    if (kitsLeftCstic) {
      kitsLeftCstic.label = "configurator.carriage.cstic.kit.left.name";
      kitsLeftCstic.info = "configurator.carriage.cstic.kit.left.info" + designSuffix;
    }
    const kitsCenterCstic = cstics[CarriageCstic.KitsCenter];
    if (kitsCenterCstic) {
      kitsCenterCstic.label = "configurator.carriage.cstic.kit.center.name";
      kitsCenterCstic.info = "configurator.carriage.cstic.kit.center.info" + designSuffix;
    }

    const kitsRightCstic = cstics[CarriageCstic.KitsRight];
    if (kitsRightCstic) {
      kitsRightCstic.label = "configurator.carriage.cstic.kit.right.name";
      // "left" on purpose
      kitsRightCstic.info = "configurator.carriage.cstic.kit.left.info" + designSuffix;
    }

    const lubricationConnectorCstic = cstics[CarriageCstic.LubricationConnector];
    if (lubricationConnectorCstic) {
      lubricationConnectorCstic.label = "configurator.carriage.cstic.lubricationConnector.name";
      lubricationConnectorCstic.values?.forEach((val) => {
        val.imageUrl = `configurator.carriage.cstic.lubricationConnector.${val.value}.image`;
      });
    }

    const lubricationConnectorStatusCstic = cstics[CarriageCstic.LubricationConnectorOrientation];
    if (lubricationConnectorStatusCstic) {
      // Label key on purpose
      lubricationConnectorStatusCstic.label = "configurator.carriage.cstic.lubricationConnector.name";
      lubricationConnectorStatusCstic.values?.forEach((val) => {
        val.label = `configurator.carriage.cstic.lubricationConnectorStatus.${val.value}.name`;
      });
    }

    return model;
  },
  postDefinitionProcessor: (model) => {
    model.allowedSteps = calculateAllowedSteps(model);
    return model;
  },
} as const;

function pageIsConfigured(model: LinearConfiguration | LinearConfiguration, page: number): boolean {
  return (model.steps[page]!.substeps ?? []).every((substepType) => {
    const substep = model.substeps[substepType];

    substep.selectedTile = findSelectedTileIndex(substep as LinearSubstep, model.kmats);

    return validateFromDefinition(
      substep as LinearSubstep,
      model.kmats,
      (model as LinearConfiguration).conflictedCstics ?? [],
    ).substepValid;
  });
}

function calculateAllowedSteps(model: LinearConfiguration | LinearConfiguration): boolean[][] {
  const canVisitStep2 = pageIsConfigured(model, 0);
  const canVisitStep3 = pageIsConfigured(model, 1);

  const allowedSteps = [
    [true, canVisitStep2, canVisitStep3],
    [true, true, canVisitStep3],
    [true, true, true],
  ];

  return allowedSteps;
}

const sendStepCompletedEvent = makeSendStepCompletedEvent(KWLT_CARRIAGE_DEFINITION);
